import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import './CustomSelectFilter.css';

/**
 * Dependent Custom Multi-Select Filter for AG Grid Server-Side Model
 *
 */
const DependentCustomSelectFilter = forwardRef((props, ref) => {
  const [appliedValues, setAppliedValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [options, setOptions] = useState([]);
  const filterChangedCallbackRef = useRef(props.filterChangedCallback);
  const previousAppliedValuesRef = useRef([]);

  const colDef = props.colDef;
  const filterParams = props.filterParams || props.colDef?.filterParams || {};
  const dependsOn = filterParams.dependsOn;
  const getOptionsFunc = filterParams.getOptions;
  const sortOptions = filterParams.sortOptions !== false; // Default to true (sort), unless explicitly set to false
  const isRestoringRef = useRef(false);

  // Use refs to store values to prevent infinite loops from new array/object references
  const allDataRef = useRef(filterParams.allData);
  const filterParamsOptionsRef = useRef(filterParams.options);

  // Update refs when values change
  useEffect(() => {
    allDataRef.current = filterParams.allData;
    filterParamsOptionsRef.current = filterParams.options;
  }, [filterParams.allData, filterParams.options]);

  // Use ref to store getOptions function to avoid triggering updates when function reference changes
  const getOptionsFuncRef = useRef(getOptionsFunc);
  useEffect(() => {
    getOptionsFuncRef.current = getOptionsFunc;
  }, [getOptionsFunc]);

  // Keep callback ref updated
  useEffect(() => {
    filterChangedCallbackRef.current = props.filterChangedCallback;
  }, [props.filterChangedCallback]);

  // Listen to parent filter changes and update options
  useEffect(() => {
    if (!dependsOn || !getOptionsFuncRef.current) {
      // No dependency - use static options
      setOptions(filterParamsOptionsRef.current || []);
      return;
    }

    // Get parent filter value from AG Grid API
    const updateOptions = () => {
      if (!getOptionsFuncRef.current) return;

      try {
        const filterModel = props.api?.getFilterModel();
        const parentFilterModel = filterModel?.[dependsOn];

        let parentValue = null;

        if (parentFilterModel?.filter) {
          // Parse parent filter value
          if (typeof parentFilterModel.filter === 'string') {
            try {
              parentValue = JSON.parse(parentFilterModel.filter);
            } catch {
              parentValue = [parentFilterModel.filter];
            }
          } else if (Array.isArray(parentFilterModel.filter)) {
            parentValue = parentFilterModel.filter;
          } else {
            parentValue = [parentFilterModel.filter];
          }
        }

        // Get new options based on parent value using the ref
        const newOptions = getOptionsFuncRef.current(parentValue, allDataRef.current);

        // Remove duplicates and optionally sort
        let uniqueOptions = [...new Set(newOptions)].filter(Boolean);
        //console.log("uniqueOptions", newOptions, uniqueOptions)
        if (sortOptions) {
          uniqueOptions = uniqueOptions.sort();
        }

        setOptions(uniqueOptions);

        // Clear selected values if they're no longer in options
        // setSelectedValues(prev => prev.filter(val => uniqueOptions.includes(val)));
        // setAppliedValues(prev => prev.filter(val => uniqueOptions.includes(val)));
        if (isRestoringRef.current) {
          isRestoringRef.current = false; // restore is done, stop protecting
        } else {
          setSelectedValues(prev => prev.filter(val => uniqueOptions.includes(val)));
          setAppliedValues(prev => prev.filter(val => uniqueOptions.includes(val)));
        }

      } catch (error) {
        console.error('Error updating dependent filter options:', error);
        setOptions([]);
      }
    };

    // Initial update
    updateOptions();

    // Listen for filter changes on parent column
    let lastParentValue = null;

    const onFilterChanged = () => {
      // Only update if the parent filter value actually changed
      const filterModel = props.api?.getFilterModel();
      const parentFilterModel = filterModel?.[dependsOn];
      const currentParentValue = JSON.stringify(parentFilterModel?.filter);

      if (currentParentValue !== lastParentValue) {
        lastParentValue = currentParentValue;
        updateOptions();
      }
    };

    if (props.api) {
      props.api.addEventListener('filterChanged', onFilterChanged);

      return () => {
        props.api.removeEventListener('filterChanged', onFilterChanged);
      };
    }
  }, [dependsOn, props.api, colDef?.field]);

  useImperativeHandle(ref, () => {
    return {
      isFilterActive() {
        return appliedValues.length > 0;
      },

      doesFilterPass(params) {
        if (appliedValues.length === 0) return true;
        const value = props.valueGetter(params.node);
        return appliedValues.includes(value);
      },

      getModel() {
        if (appliedValues.length === 0) return null;

        return {
          filterType: 'text',
          type: 'inArray',
          filter: JSON.stringify(appliedValues),
        };
      },

      setModel(model) {
        if (model == null) {
          setAppliedValues([]);
          setSelectedValues([]);
        } else if (model.filter) {
          let values;

          if (Array.isArray(model.filter)) {
            values = model.filter;
          } else if (typeof model.filter === 'string') {
            try {
              const parsed = JSON.parse(model.filter);
              values = Array.isArray(parsed) ? parsed : [parsed];
            } catch (e) {
              values = [model.filter];
            }
          } else {
            values = [model.filter];
          }
          isRestoringRef.current = true;  
          setAppliedValues(values);
          setSelectedValues(values);
        }
      },

      getModelAsString(model) {
        if (!model || !model.filter) return '';

        try {
          const values = typeof model.filter === 'string'
            ? JSON.parse(model.filter)
            : model.filter;

          if (Array.isArray(values) && values.length > 0) {
            return values.length === 1
              ? values[0]
              : `${values.length} selected`;
          }
        } catch (e) {
          return '';
        }

        return '';
      },

      afterGuiAttached(params) {
        // Optional: focus search input
      },

      afterGuiDetached() {
        // Cleanup if needed
      },
    };
  });

  // Trigger filter change only when applied values actually change
  useEffect(() => {
    const previousValues = previousAppliedValuesRef.current;

    const valuesChanged =
      appliedValues.length !== previousValues.length ||
      appliedValues.some((val, index) => val !== previousValues[index]);

    previousAppliedValuesRef.current = appliedValues;

    if (!valuesChanged) {
      return;
    }

    filterChangedCallbackRef.current();
  }, [appliedValues, colDef?.field]);

  const handleCheckboxChange = (option) => {
    setSelectedValues((prev) => {
      if (prev.includes(option)) {
        return prev.filter((v) => v !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      setSelectedValues([]);
    } else {
      setSelectedValues([...filteredOptions]);
    }
  };

  const handleApply = () => {
    setAppliedValues([...selectedValues]);
  };

  const handleReset = () => {
    setSelectedValues([...appliedValues]);
    setSearchText('');
  };

  // Filter options based on search text
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="custom-select-filter">
      {/* Dependency notice */}
      {dependsOn && (
        <div className="filter-dependency-notice">
          Filtered by: {dependsOn}
        </div>
      )}

      {/* Search input */}
      <div className="filter-search">
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="filter-search-input"
        />
      </div>

      {/* Select All */}
      <div className="filter-actions">
        <label className="filter-checkbox-item">
          <input
            type="checkbox"
            checked={selectedValues.length === filteredOptions.length && filteredOptions.length > 0}
            onChange={handleSelectAll}
          />
          <span className="filter-checkbox-label">
            (Select All)
          </span>
        </label>
      </div>

      {/* Options list */}
      <div className="filter-options-list">
        {filteredOptions.length === 0 ? (
          <div className="filter-no-results">
            {dependsOn ? 'No options for selected filter' : 'No options found'}
          </div>
        ) : (
          filteredOptions.map((option, index) => (
            <label key={index} className="filter-checkbox-item">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleCheckboxChange(option)}
              />
              <span className="filter-checkbox-label">{option}</span>
            </label>
          ))
        )}
      </div>

      {/* Footer buttons */}
      <div className="filter-footer-buttons">
        <button onClick={handleReset} className="filter-reset-btn">
          Reset
        </button>
        <button onClick={handleApply} className="filter-apply-btn">
          Apply
        </button>
      </div>
    </div>
  );
});

DependentCustomSelectFilter.displayName = 'DependentCustomSelectFilter';

export default DependentCustomSelectFilter;
